import type { DeviceEnrolment, Group, Kit, LegalStatus, User } from "@/types";

/**
 * The owner's first-run checklist — five things, derived from what exists.
 *
 * Nothing is recorded. A teacher who was added yesterday ticks the second
 * step whether or not the owner ever saw this list, and an institution that
 * imported its class list before the checklist existed finds the third step
 * already done. Derived state cannot drift from the truth, and it means there
 * is no new backend for a panel that exists to be outgrown.
 */
export type OwnerStepId = "legal" | "teachers" | "group" | "devices" | "composer";

export interface OwnerStep {
  id: OwnerStepId;
  done: boolean;
  /** Where the step is done. An absolute URL leaves the app. */
  to: string;
  external?: boolean;
  labelKey: string;
  /** One line under the label, when the state has something to add. */
  hintKey?: string;
  hintParams?: Record<string, unknown>;
}

export interface OwnerChecklist {
  steps: OwnerStep[];
  doneCount: number;
  total: number;
  complete: boolean;
}

export interface ChecklistInput {
  /** Undefined while the legal file is still loading — the step stays open. */
  legal: LegalStatus | undefined;
  people: readonly User[];
  groups: readonly Group[];
  devices: readonly DeviceEnrolment[];
  kits: readonly Kit[];
  /** Whether the institution has written a single scene, in Composer. */
  hasScenes: boolean;
  composerUrl: string;
}

export function ownerChecklist(input: ChecklistInput): OwnerChecklist {
  const outstanding = input.legal?.outstanding.length ?? 0;
  const legalDone = input.legal !== undefined && outstanding === 0;

  const teachers = input.people.filter((person) => person.role === "teacher" && !person.disabled);
  const players = input.people.filter((person) => person.role === "student" && !person.disabled);
  const live = input.groups.filter((group) => !group.archivedAt);
  const withPlayers = live.find((group) => group.roster.length > 0);
  const emptyGroup = live.find((group) => group.roster.length === 0);

  const deviceInService = input.devices.some((device) => !device.revokedAt);

  /*
    The third step has three doors, and the one offered follows the state:
    nobody to put in a group yet → the people page; players but no group →
    the new-group form; a group with nobody on it → that group. Sending an
    owner to "create a group" when what they lack is players would be a
    checklist that knows less than the owner does.
  */
  const groupStep: OwnerStep = withPlayers
    ? { id: "group", done: true, to: `/groups/${withPlayers.id}`, labelKey: "onboarding.step.group" }
    : emptyGroup
      ? {
          id: "group",
          done: false,
          to: `/groups/${emptyGroup.id}`,
          labelKey: "onboarding.step.group",
          hintKey: "onboarding.step.group.hintEmptyGroup",
          hintParams: { name: emptyGroup.name },
        }
      : players.length === 0
        ? {
            id: "group",
            done: false,
            to: "/people",
            labelKey: "onboarding.step.group",
            hintKey: "onboarding.step.group.hintNoPlayers",
          }
        : {
            id: "group",
            done: false,
            to: "/groups/new",
            labelKey: "onboarding.step.group",
            hintKey: "onboarding.step.group.hintNoGroup",
          };

  const steps: OwnerStep[] = [
    {
      id: "legal",
      done: legalDone,
      to: "/legal",
      labelKey: "onboarding.step.legal",
      ...(legalDone || input.legal === undefined
        ? {}
        : {
            hintKey:
              outstanding === 1
                ? "onboarding.step.legal.hint"
                : "onboarding.step.legal.hintPlural",
            hintParams: { count: outstanding },
          }),
    },
    {
      id: "teachers",
      done: teachers.length > 0,
      to: "/people/new",
      labelKey: "onboarding.step.teachers",
      hintKey: "onboarding.step.teachers.hint",
    },
    groupStep,
    {
      id: "devices",
      done: input.kits.length > 0 || deviceInService,
      to: "/kits",
      labelKey: "onboarding.step.devices",
      hintKey: "onboarding.step.devices.hint",
    },
    {
      id: "composer",
      done: input.hasScenes,
      to: input.composerUrl,
      external: true,
      labelKey: "onboarding.step.composer",
      hintKey: "onboarding.step.composer.hint",
    },
  ];

  const doneCount = steps.filter((step) => step.done).length;
  return { steps, doneCount, total: steps.length, complete: doneCount === steps.length };
}
